import type { InclusiveOptions, InclusiveState } from '../types';

export async function fetchConfig(
    options: InclusiveOptions,
): Promise<Record<string, unknown> | null> {
    const base = options.apiBaseUrl ?? '';

    try {
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 10000);
        const response = await fetch(`${base}/api/v1/sdk/config`, {
            headers: {
                'X-Application-Id': options.applicationId,
                'X-Sdk-Version': '1.0.0',
            },
            signal: controller.signal,
        });
        window.clearTimeout(timer);

        if (!response.ok) {
            return null;
        }

        return (await response.json()) as Record<string, unknown>;
    } catch {
        return null;
    }
}

export async function requestTextToSpeech(
    options: InclusiveOptions,
    text: string,
    language: string,
    signal?: AbortSignal,
    engine: 'sunbird' | 'azure' | 'google' = 'sunbird',
    speakingRate?: number,
): Promise<string | null> {
    const base = options.apiBaseUrl ?? '';

    if (signal?.aborted) {
        return null;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 60000);
    const abort = (): void => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });

    try {
        const response = await fetch(`${base}/api/v1/language/tts`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Application-Id': options.applicationId,
                'X-Sdk-Version': '1.0.0',
            },
            body: JSON.stringify({
                text,
                language,
                engine,
                speakingRate,
            }),
            signal: controller.signal,
        });

        if (!response.ok) {
            return null;
        }

        const payload = (await response.json()) as {
            data?: { audioUrl?: string };
        };

        return payload.data?.audioUrl ?? null;
    } catch {
        return null;
    } finally {
        window.clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
    }
}

export async function requestTranslate(
    options: InclusiveOptions,
    text: string,
    sourceLanguage: string,
    targetLanguage: string,
    signal?: AbortSignal,
): Promise<string | null> {
    const base = options.apiBaseUrl ?? '';

    if (signal?.aborted) {
        return null;
    }

    const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 120000);
    const abort = (): void => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });

    try {
        const response = await fetch(`${base}/api/v1/language/translate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Application-Id': options.applicationId,
                'X-Sdk-Version': '1.0.0',
            },
            body: JSON.stringify({ text, sourceLanguage, targetLanguage }),
            signal: controller.signal,
        });

        if (!response.ok) {
            return null;
        }

        const payload = (await response.json()) as {
            data?: { text?: string };
        };

        return payload.data?.text ?? null;
    } catch {
        return null;
    } finally {
        window.clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
    }
}

export async function persistPreferences(state: InclusiveState): Promise<void> {
    const base = state.options.apiBaseUrl ?? '';

    try {
        await fetch(`${base}/api/v1/preferences`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'X-Application-Id': state.options.applicationId,
                'X-Sdk-Version': '1.0.0',
            },
            body: JSON.stringify({
                sessionId: state.sessionId,
                profile: state.profile,
                language: state.language,
                settings: state.settings,
            }),
        });
    } catch {
        // Cloud preferences are optional.
    }
}
